import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import { CONFIG } from '@frontend/config';
import {
  apiErrorMessage,
  isProvenanceExpired,
  RailSearchPayload,
  useConnectorCapabilities,
  usePassesForOffer,
  usePlaceCoords,
  useSearchRail,
} from '../queries';
import {
  cleanParams,
  isIncompleteSearch,
  UrlParams,
  useSearchUrlState,
} from '../useSearchUrlState';
import { PASSES_BOOKING_BLOCKED, PassOffer, isContinuous, productTitle } from '../passes';
import {
  RailOffer,
  formatLocalDate,
  formatLocalTime,
  groupRailJourneys,
  fareLabel,
  railPlaceFromParams,
  railRouteMapPoints,
  railRouteParams,
} from '../rail';
import { PlaceRef } from '../places';
import { committedPlaceCoordsIndex, committedPlaceId } from '../placeCoords';
import { CountrySelect } from './CountrySelect';
import { PriceBlock } from './PriceBlock';
import { RailJourneyCard } from './RailJourneyCard';
import { PlaceAutocomplete } from './PlaceAutocomplete';
import { describeRail } from '../describeSearch';
import { SearchBar, SearchDivider, SearchField } from './SearchBar';
import {
  SEARCH_DATE_FIELD_WIDTH,
  SEARCH_FIELD_WIDE_SHARE,
  SEARCH_PASSENGER_FIELD_WIDTH,
} from '../searchFieldLayout';
import { RecentSearches } from './RecentSearches';
import { BookingModal } from './BookingModal';
import {
  committedParty,
  railBookingPassengerCount,
} from '../committedBookingContext';
import { captureSelectedQuote, quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import {
  useSearchResultAuthority,
  useSearchSelectionBlockedReason,
} from '../useSearchResultAuthority';
import { isQuoteFreshForHold, useFareCountdown } from '../useFareCountdown';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import { searchRouteMapKey } from '../routeMap';
import { SearchRouteMap } from './SearchRouteMap';
import {
  DEFAULT_RAIL_PASSENGER,
  RailPassengerDraft,
  authorizedRailSearchPayloadFromParams,
  railDraftsFromParam,
  railDraftParam,
  railPassPassengers,
  railPassResultsAreCurrent,
  railPassengersFromDraft,
  railSearchPayloadFromParams,
  railSupportedPassengerTypes,
  railTypedPassengersEnabled,
} from '../railPassengers';
import {
  railPassengerRules,
  validateSearchPassengers,
} from '../searchPassengers';
import { SearchPassengerPicker } from './SearchPassengerPicker';
import { useBookingCaseContext } from './BookingCaseContext';
import { todaySearchDate } from '../searchDates';
import { SearchDatePicker } from './SearchDatePicker';

/** La residencia del pasajero se repite entre búsquedas del mismo asesor. */
const PASS_RESIDENCE_KEY = 'travelServices.passes.residence';

/**
 * Buscador de trenes. Las estaciones se
 * eligen contra el maestro de lugares (`PlaceAutocomplete`, `servedBy` de los
 * conectores rail configurados) y la búsqueda viaja con el `plc_*`. El proveedor cotiza EN
 * VIVO en ambos casos: la búsqueda puede tardar ~45s, así que la espera se
 * comunica explícitamente.
 */
/** El payload desde la URL; `null` deja la query deshabilitada. */
export function payloadFromParams(
  params: Record<string, string>,
): RailSearchPayload | null {
  return railSearchPayloadFromParams(params);
}

export function RailSearchForm() {
  const { t, i18n } = useTranslation();
  const url = useSearchUrlState({ vertical: 'rail', dateParam: 'fecha' });
  const { data: connectors } = useConnectorCapabilities();
  const searchPayload = useMemo(
    () => authorizedRailSearchPayloadFromParams(url.params, connectors),
    [url.params, connectors],
  );
  const search = useSearchRail(searchPayload);
  const typedPassengersEnabled = railTypedPassengersEnabled(connectors);
  const supportedPassengerTypes = railSupportedPassengerTypes(connectors);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload ? quoteSearchIdentity('rail', CONFIG.AGENCY_ID, searchPayload) : undefined;
  const [fromPlace, setFromPlace] = useState<PlaceRef | null>(() =>
    railPlaceFromParams(url.params.origen, url.params.origenNombre));
  const [toPlace, setToPlace] = useState<PlaceRef | null>(() =>
    railPlaceFromParams(url.params.destino, url.params.destinoNombre));
  const fromPoint = fromPlace;
  const toPoint = toPlace;
  const [date, setDate] = useState(() => url.params.fecha ?? '');
  const [typedPassengers, setTypedPassengers] = useState<RailPassengerDraft[]>(
    () => railDraftsFromParam(url.params.pax),
  );
  const [typedPassengersEdited, setTypedPassengersEdited] = useState(false);
  const { partySuggestion, consumePartySuggestion } = useBookingCaseContext();
  useEffect(() => {
    if (!partySuggestion) return;
    setTypedPassengers(railDraftsFromParam(String(partySuggestion.size)));
    setTypedPassengersEdited(true);
    consumePartySuggestion(partySuggestion.revision);
  }, [partySuggestion, consumePartySuggestion]);
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<RailOffer> | null
  >(null);
  const bookingOffer = bookingQuote?.offer ?? null;
  /** Viaje para el que se están mirando pases (null = panel cerrado). */
  const [passesFor, setPassesFor] = useState<RailOffer | null>(null);
  const [passesGeneration, setPassesGeneration] = useState<{
    quotedAt: number;
    searchIdentity: string;
  } | null>(null);
  const [passBookingQuote, setPassBookingQuote] = useState<
    SelectedTravelQuote<PassOffer> | null
  >(null);
  const passBooking = passBookingQuote?.offer ?? null;
  const passesForOffer = usePassesForOffer();
  const [passResidence, setPassResidence] = useState(
    () => localStorage.getItem(PASS_RESIDENCE_KEY) ?? '',
  );
  const committedPassengers = railBookingPassengerCount(searchPayload);
  const typedRailMissingAges = Boolean(
    Array.isArray(searchPayload?.passengers) &&
      searchPayload.passengers.some((passenger) => passenger.age === undefined),
  );
  const currentPassPassengers = railPassPassengers(
    searchPayload?.passengers,
    passResidence,
  );
  const currentPassSearchIdentity = passesFor && passResidence && currentPassPassengers
    ? quoteSearchIdentity('passes', CONFIG.AGENCY_ID, {
      provenanceToken: passesFor.provenance,
      passengers: currentPassPassengers,
    })
    : undefined;
  const passResultsAreCurrent = railPassResultsAreCurrent(
    passesGeneration?.searchIdentity,
    currentPassSearchIdentity,
  );
  /**
   * Estaciones del pedido para el mapa. Cada referencia canónica se resuelve
   * contra Places; Bandido no consulta ni conserva ids nativos de estación.
   */
  const committedFromPlaceId = committedPlaceId(searchPayload?.fromStation);
  const committedToPlaceId = committedPlaceId(searchPayload?.toStation);
  const fromPlaceCoords = usePlaceCoords(committedFromPlaceId);
  const toPlaceCoords = usePlaceCoords(committedToPlaceId);
  const railPlaceCoords = useMemo(
    () =>
      committedPlaceCoordsIndex([
        {
          id: committedFromPlaceId,
          selection: fromPlace,
          resolved: fromPlaceCoords.data,
        },
        {
          id: committedToPlaceId,
          selection: toPlace,
          resolved: toPlaceCoords.data,
        },
      ]),
    [
      committedFromPlaceId,
      committedToPlaceId,
      fromPlace,
      toPlace,
      fromPlaceCoords.data,
      toPlaceCoords.data,
    ],
  );
  const bookingRoutePoints = railRouteMapPoints(
    searchPayload,
    {
      placeCoords: railPlaceCoords,
      label: url.params.origenNombre,
    },
    {
      placeCoords: railPlaceCoords,
      label: url.params.destinoNombre,
    },
  );

  const typedPassengerPayload = railPassengersFromDraft(
    typedPassengers,
    supportedPassengerTypes,
  );
  const passengerRules = railPassengerRules(
    supportedPassengerTypes,
    typedPassengersEnabled,
  );
  const passengerValidation = validateSearchPassengers(
    typedPassengers,
    passengerRules,
  );
  const minDate = todaySearchDate();
  const canSearch = Boolean(
    fromPoint &&
      toPoint &&
      date &&
      date >= minDate &&
      passengerValidation.valid &&
      (!typedPassengersEnabled || typedPassengerPayload),
  );

  /**
   * El formulario tal como se escribiría en la URL. Es la ÚNICA derivación del
   * borrador: de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22), no dos expresiones parecidas.
   */
  const formParams: UrlParams = {
    buscar: '1',
    ...(fromPoint && toPoint ? railRouteParams(true, fromPoint, toPoint) : {}),
    fecha: date,
    pax: typedPassengersEnabled
      ? railDraftParam(url.params.pax, typedPassengers, typedPassengersEdited)
      : String(typedPassengers.length),
  };
  const draftPayload = authorizedRailSearchPayloadFromParams(
    cleanParams(formParams),
    connectors,
  );
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);
  const selectionBlockedReason = useSearchSelectionBlockedReason(resultAuthority);

  /** Vacía la búsqueda: campos, URL y memoria de navegación. */
  const handleClear = () => {
    setFromPlace(null);
    setToPlace(null);
    setDate('');
    setTypedPassengers([{ ...DEFAULT_RAIL_PASSENGER }]);
    setTypedPassengersEdited(false);
    url.reset();
  };

  const handleSearch = () => {
    if (!fromPoint || !toPoint || !date) return;
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('rail', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  const journeys = useMemo(
    () => groupRailJourneys(search.data?.results ?? []),
    [search.data],
  );

  /**
   * Cross-sell: el proveedor calcula qué pases cubren ESTE viaje. Se abre en un
   * panel aparte para no perder los resultados de la búsqueda de trenes.
   */
  const handleSeePasses = (fare: RailOffer) => {
    if (formDiverged || !isQuoteFreshForHold(search.dataUpdatedAt, Date.now())) return;
    setPassesGeneration(null);
    passesForOffer.reset();
    setPassesFor(fare);
    if (passResidence) searchPasses(fare, passResidence);
  };

  /**
   * El cross-sell EXIGE pasajeros (el hub responde 400 sin ellos) y la
   * residencia define el producto —Interrail para residentes europeos, Eurail
   * para el resto—, así que se pide explícitamente y se recuerda.
   */
  const searchPasses = (fare: RailOffer, residence: string) => {
    setPassesGeneration(null);
    passesForOffer.reset();
    const passengers = railPassPassengers(searchPayload?.passengers, residence);
    if (!passengers) return;
    localStorage.setItem(PASS_RESIDENCE_KEY, residence);
    const request = {
      provenanceToken: fare.provenance,
      passengers,
    };
    passesForOffer.mutate(
      request,
      // Los pases se cotizan DESPUÉS que los trenes y con su propio provenance:
      // usar el reloj de la búsqueda de trenes mostraría menos tiempo del que
      // hay y podría bloquear una reserva todavía válida.
      {
        onSuccess: () =>
          setPassesGeneration({
            quotedAt: Date.now(),
            searchIdentity: quoteSearchIdentity(
              'passes',
              CONFIG.AGENCY_ID,
              request,
            ),
          }),
      },
    );
  };

  const handleBook = (offer: RailOffer) => {
    setBookingQuote(
      captureSelectableSearchQuote(
        offer,
        search.dataUpdatedAt,
        'rail',
        CONFIG.AGENCY_ID,
        searchPayload,
        Date.now(),
        formDiverged,
      ),
    );
  };

  const passOffers = (passResultsAreCurrent
    ? passesForOffer.data?.results ?? []
    : []) as PassOffer[];
  const passFailures = [
    ...(passResultsAreCurrent
      ? passesForOffer.data?.providers?.failed.map((f) => f.provider) ?? []
      : []),
    ...(passResultsAreCurrent ? passesForOffer.data?.providers?.timedOut ?? [] : []),
  ];

  const bookingDetails = bookingOffer
    ? [
      `${formatLocalDate(bookingOffer.departure as string)} · ${formatLocalTime(
          bookingOffer.departure as string,
      )} ${bookingOffer.fromStation as string} → ${formatLocalTime(
          bookingOffer.arrival as string,
      )} ${bookingOffer.toStation as string}`,
      `${bookingOffer.carrier as string} · ${fareLabel(bookingOffer)} · ${t(
        'travelServices.pax',
      )}: ${committedPassengers}`,
      // Las condiciones de la tarifa van a la vista antes de confirmar: son
      // la letra chica (cambios/devolución) que se le transmite al pasajero.
      ...(bookingOffer.fareConditions ?? []).map(
        (condition) =>
          `${t(`travelServices.rail.conditionTypes.${condition.type}`, {
            defaultValue: condition.type,
          })}: ${condition.description}`,
      ),
    ]
    : undefined;

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
      >
        <SearchField label={t('travelServices.rail.from')} grow width={200}>
          <PlaceAutocomplete
            label={t('travelServices.rail.from')}
            vertical="rail"
            value={fromPlace}
            onChange={setFromPlace}
            width="100%"
            required
          />
        </SearchField>
        <Tooltip title={t('travelServices.transfers.swap')}>
          <IconButton
            size="small"
            sx={{ alignSelf: 'center', mx: 0.5 }}
            onClick={() => {
              setFromPlace(toPlace);
              setToPlace(fromPlace);
            }}
          >
            <SwapHoriz />
          </IconButton>
        </Tooltip>
        <SearchField label={t('travelServices.rail.to')} grow width={200}>
          <PlaceAutocomplete
            label={t('travelServices.rail.to')}
            vertical="rail"
            value={toPlace}
            onChange={setToPlace}
            width="100%"
            required
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.transfers.date')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_WIDE_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.transfers.date')}
            value={date}
            onChange={setDate}
            minDate={minDate}
            required
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.passengerPicker.label')}
          width={SEARCH_PASSENGER_FIELD_WIDTH}
        >
          <SearchPassengerPicker
            value={typedPassengers}
            onChange={(nextPassengers) => {
              setTypedPassengersEdited(true);
              setTypedPassengers(nextPassengers);
            }}
            rules={passengerRules}
          />
        </SearchField>
      </SearchBar>

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="rail"
          describe={(params) => describeRail(params, t, i18n.language)}
        />
      )}

      {/* El proveedor cotiza en vivo: la espera es larga y hay que decirlo. */}
      {/* La URL pide una búsqueda que no se puede ejecutar (link
          recortado o editado a mano): decirlo, no quedarse mudo. */}
      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describeRail(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {/* El recorrido de la búsqueda comprometida, uno por búsqueda y
          colapsado: el iframe no existe hasta que el asesor lo abre. La
          identidad de la búsqueda va de `key` para que una búsqueda nueva
          arranque cerrada y sin el mapa de la anterior. */}
      {url.params.buscar === '1' && searchPayload && (
        <SearchRouteMap
          key={searchRouteMapKey(activeSearchIdentity, bookingRoutePoints)}
          vertical="rail"
          points={bookingRoutePoints}
        />
      )}

      {search.isLoading && (
        <>
          {/* El proveedor cotiza en vivo con los operadores y puede tardar un
              minuto: se dice, además de dibujar el esqueleto. */}
          <Typography variant="body2" color="text.secondary">
            {t('travelServices.rail.searchingLong')}
          </Typography>
          <ResultsSkeleton archetype="schedule" />
        </>
      )}

      {search.isError && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}

      <ProviderStatusStrip providers={search.data?.providers} />

      {search.data && !search.isLoading && (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('travelServices.rail.journeysFound', { count: journeys.length })}
          </Typography>
          {journeys.length === 0 ? (
            <Alert severity="info">{t('travelServices.noResults')}</Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {journeys.map((journey, index) => (
                <RailJourneyCard
                  key={journey.key}
                  journey={journey}
                  index={index}
                  selectionDisabled={!resultAuthority.canSelectResult}
                  selectionDisabledReason={selectionBlockedReason}
                  onBook={handleBook}
                  onSeePasses={handleSeePasses}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {!search.data && !search.isLoading && !search.isError && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.rail.emptyState')}
        </Typography>
      )}

      {/* Pases que cubren el viaje elegido. El precio de un pase suele ser
          MAYOR que el del boleto suelto: conviene solo si el pasajero hace
          varios trayectos, y por eso se muestra como alternativa, no como
          reemplazo. */}
      <Dialog
        open={Boolean(passesFor)}
        onClose={() => setPassesFor(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('travelServices.passes.crossSell')}</DialogTitle>
        <DialogContent>
          {/* La residencia define el producto y el precio: sin ella el
              proveedor no devuelve nada. */}
          <Box mb={2} mt={0.5}>
            <CountrySelect
              fullWidth
              required
              label={t('travelServices.passes.residence')}
              value={passResidence}
              onChange={(code) => {
                setPassResidence(code ?? '');
                if (code && passesFor) searchPasses(passesFor, code);
              }}
            />
          </Box>
          {!passResidence && (
            <Alert severity="info">{t('travelServices.passes.residenceNeeded')}</Alert>
          )}
          {typedRailMissingAges && (
            <Alert severity="info">{t('travelServices.rail.ageNeededForPasses')}</Alert>
          )}
          {passesForOffer.isLoading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          )}
          {/* Un token vencido NO es "no hay pases": hay que re-buscar el tren. */}
          {passesForOffer.isError && (
            <Alert
              severity={
                isProvenanceExpired(passesForOffer.error) ? 'warning' : 'info'
              }
            >
              {isProvenanceExpired(passesForOffer.error)
                ? t('travelServices.booking.provenanceExpired')
                : (apiErrorMessage(passesForOffer.error) ??
                  t('travelServices.passes.crossSellEmpty'))}
            </Alert>
          )}
          {/* El envelope trae el estado del fan-out: un proveedor caído NO es
              lo mismo que "no hay pases para este viaje". */}
          {passFailures.length > 0 && (
            <Alert severity="warning">
              {t('travelServices.providersFailed', {
                providers: passFailures.join(', '),
              })}
            </Alert>
          )}
          {passResultsAreCurrent && passOffers.length === 0 && passFailures.length === 0 && (
            <Alert severity="info">{t('travelServices.passes.crossSellEmpty')}</Alert>
          )}
          {passOffers.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('travelServices.passes.crossSellHint')}
              </Typography>
              <Stack spacing={1}>
                {passOffers.slice(0, 12).map((pass, index) => (
                  <Box
                    key={`${pass.provenance.slice(0, 10)}-${index}`}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    px={1.5}
                    py={1}
                    bgcolor="grey.100"
                    borderRadius={1}
                  >
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2">
                        {productTitle(pass, i18n.language)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isContinuous(pass)
                          ? t('travelServices.passes.continuous')
                          : t('travelServices.passes.travelDaysIn', {
                            days: pass.travelDayCount,
                            validity: pass.validityDays,
                          })}
                      </Typography>
                    </Box>
                    <PriceBlock pricing={pass.pricing} size="small" align="right" />
                    <Box textAlign="right">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={PASSES_BOOKING_BLOCKED}
                        onClick={() => {
                          if (passesGeneration) {
                            setPassBookingQuote(
                              captureSelectedQuote(
                                pass,
                                passesGeneration.quotedAt,
                                passesGeneration.searchIdentity,
                              ),
                            );
                          }
                          setPassesFor(null);
                        }}
                      >
                        {t('travelServices.book')}
                      </Button>
                      {/* Bloqueada ≠ muda: el motivo va pegado al botón, acá
                          igual que en la card de pases. */}
                      {PASSES_BOOKING_BLOCKED && (
                        <Tooltip title={t('travelServices.passes.bookingBlocked')}>
                          <Typography
                            color="text.secondary"
                            sx={{ display: 'block', fontSize: 10.5, mt: '2px', cursor: 'help' }}
                          >
                            {t('travelServices.passes.quoteOnly')}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassesFor(null)}>
            {t('travelServices.booking.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <BookingModal
        vertical="passes"
        quote={passBookingQuote}
        activeSearchIdentity={passBookingQuote?.searchIdentity}
        details={
          passBooking
            ? [
              productTitle(passBooking, i18n.language),
              t('travelServices.passes.ticketNotice'),
            ]
            : undefined
        }
        /* Cross-sell de pases DESDE trenes: la composición es la de la
           búsqueda de trenes que lo produjo, no la de una búsqueda de pases
           (que en esta pantalla no existe). */
        party={committedParty({ vertical: 'rail', payload: searchPayload })}
        onClose={() => setPassBookingQuote(null)}
      />

      <BookingModal
        vertical="rail"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        details={bookingDetails}
        routePoints={bookingRoutePoints}
        party={committedParty({ vertical: 'rail', payload: searchPayload })}
        onClose={() => setBookingQuote(null)}
      />
    </Box>
  );
}
